import React, { useState } from 'react'
import { FaTrash, FaPlus } from 'react-icons/fa'
import { Box, Stack, Button, Heading, Text, Input, Checkbox, Card, Icon, EmptyCard, Alert } from '@/components/lib'
import api from '../../../api'
import utils, { SIGNATURE_MEANINGS } from '@/utils/utils'
import useStore from '../../../store'

// Casebook settings: whether responses can be electronically signed on this
// trial at all (off until a study opts in, so nobody sees signature bars,
// cards or badges they never asked for), the meanings a signer chooses
// from, and whether every casebook must be signed before the database can
// be locked. See docs/plans/electronic-signatures.md.

export default function Casebook ({ trial }) {
  const { user, updateTrial } = useStore()
  const canManage = utils.canManageTrial(user, trial)
  const enabled = !!trial.responseSigningEnabled
  const meanings = trial.signatureMeanings?.length ? trial.signatureMeanings : SIGNATURE_MEANINGS
  const [newMeaning, setNewMeaning] = useState('')

  function save (data, then) {
    api.trials.update(trial._id, data, t => { updateTrial(t._id, t); if (then) then() }, err => utils.toast('error', err.message))
  }

  function addMeaning () {
    const text = newMeaning.trim()
    if (!text) return
    if (meanings.includes(text)) return utils.toast('error', 'That meaning is already in the list')
    save({ signatureMeanings: [...meanings, text] }, () => setNewMeaning(''))
  }

  function removeMeaning (text) {
    const remaining = meanings.filter(m => m !== text)
    if (!remaining.length) return utils.toast('error', 'Keep at least one meaning', 'Signers have to choose one when they sign.')
    save({ signatureMeanings: remaining })
  }

  return (
    <Stack gap={5}>
      <EmptyCard icon='signature' title='Casebook and electronic signatures' description='Let the investigator sign responses and whole casebooks, attesting that the data is accurate and complete.' />

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Response signing</Heading>
        <Checkbox isChecked={enabled} disabled={!canManage} onChange={e => save({ responseSigningEnabled: e.checked })}>Enable response and casebook signing</Checkbox>
        <Text fontSize='sm'>
          When enabled, users holding the <b>Electronically sign responses</b> permission (<code>trial.signResponses</code>) see a <b>Sign</b> button under each response and a <b>Casebook</b> card on each participant&apos;s page.
        </Text>
        <Text fontSize='sm' color='fg.muted'>
          Turning this off hides signing. Signatures already made are kept.
        </Text>
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Signature meanings</Heading>
        <Text>What a signer is attesting to. They choose one of these when they sign, and it is recorded with the signature.</Text>
        <Stack gap={1}>
          {meanings.map(m =>
            <Stack key={m} direction='row' alignItems='center' justifyContent='space-between' p={2} rounded='md' bg='bg.subtle'>
              <Text>{m}</Text>
              {canManage && enabled &&
                <Button size='2xs' variant='ghost' onClick={() => removeMeaning(m)} aria-label='Remove'><Icon as={FaTrash} /></Button>}
            </Stack>)}
        </Stack>
        {canManage && enabled && meanings.length < 10 &&
          <Stack direction='row' gap={2}>
            <Input placeholder='Add a meaning, e.g. "Reviewed by the monitor"' value={newMeaning} onChange={e => setNewMeaning(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') addMeaning() }} maxLength={200} />
            <Button onClick={addMeaning} disabled={!newMeaning.trim()}><Icon as={FaPlus} mr={1} /> Add</Button>
          </Stack>}
        {!trial.signatureMeanings?.length &&
          <Text fontSize='sm' color='fg.muted'>These are the defaults. Adding or removing one saves your own list for this trial.</Text>}
      </Card>

      <Card variant='outline' gap={2}>
        <Heading as='h3' size='md'>Database lock</Heading>
        <Checkbox isChecked={enabled && !!trial.signaturesRequiredForLock} disabled={!canManage || !enabled} onChange={e => save({ signaturesRequiredForLock: e.checked })}>Require every casebook to be signed before the database can be locked</Checkbox>
        <Text>
          With this on, locking the database is refused while any participant who has submitted responses has an unsigned or partly signed casebook.
        </Text>
        {!enabled && <Alert status='info' variant='left-accent'>Enable response signing above to use this.</Alert>}
      </Card>

      <Box>
        <Text fontSize='sm' color='fg.muted'>
          Every signature records who, when (UTC), the meaning, how the signer confirmed, the version of Trialflare and a fingerprint of the response. Amending a signed response supersedes the signature and emails the signer. Signing and supersession are in the event log, and the study record export lists every signature.
        </Text>
      </Box>
    </Stack>
  )
}
